import type { Services } from '~/services/types'
import type { StorageModules } from '~/storage/types'
import type { Vendor, Invoice } from '~/storage/types'
import type { ParsedInvoiceMetadata } from '~common/invoice-processing/types'
import {
    isAnyExactMatch,
    calculateStringSimilarity,
} from '~/features/string-similarity'
import { doArraysOverlap } from '~/utils/array-utils'
import {
    CONFIDENCE_SCORES,
    MAX_VENDOR_SUGGESTIONS,
    LOW_CONFIDENCE_FILTER_THRESHOLD,
    HIGH_CONFIDENCE_AUTO_ASSOCIATE_THRESHOLD,
} from './constants'
import type { InvoiceUpdateParams } from './types'

export interface InvoiceVendorMatchingServiceDeps {
    getServices: () => Pick<Services, 'errorTracker' | 'cache'>
    storageModules: StorageModules
}

export class InvoiceVendorMatchingService {
    constructor(private readonly deps: InvoiceVendorMatchingServiceDeps) {}

    /**
     * Calculate name similarity score between extracted name and vendor aliases
     */
    private calcNameSimilarity(
        extractedName: string,
        vendorAliases: string[] | null | undefined,
    ): number {
        if (!extractedName || !vendorAliases || vendorAliases.length === 0) {
            return 0
        }

        const similarities = vendorAliases.map((alias) =>
            calculateStringSimilarity(extractedName, alias),
        )

        // Return the highest similarity score, scaled to the name similarity scale
        const maxSimilarity = Math.max(...similarities)
        return Math.floor(
            maxSimilarity * CONFIDENCE_SCORES.NAME_SIMILARITY_SCALE,
        )
    }

    /**
     * Score a single vendor against invoice extraction data
     */
    private scoreVendor(
        invoice: ParsedInvoiceMetadata,
        vendor: Vendor,
    ): number {
        let score = 0

        // IBAN match - highest priority
        if (doArraysOverlap(invoice.vendorIbans, vendor.ibans)) {
            score += CONFIDENCE_SCORES.IBAN
        }

        // Email match - high priority
        if (
            doArraysOverlap(invoice.vendorEmailAddresses, vendor.emailAddresses)
        ) {
            score += CONFIDENCE_SCORES.EMAIL
        }

        // Name matching
        if (invoice.vendorName) {
            // Exact match bonus
            if (isAnyExactMatch(invoice.vendorName, vendor.aliases)) {
                score += CONFIDENCE_SCORES.NAME
            }

            // Similarity score
            score += this.calcNameSimilarity(invoice.vendorName, vendor.aliases)
        }

        // Country penalty - heavily penalize mismatches
        if (vendor.country && invoice.vendorCountry) {
            const vendorCountryLower = vendor.country.toLowerCase()
            const invoiceCountryLower = invoice.vendorCountry.toLowerCase()
            if (vendorCountryLower !== invoiceCountryLower) {
                score += CONFIDENCE_SCORES.COUNTRY_MISMATCH_PENALTY
            }
        }

        return Math.max(0, score)
    }

    /**
     * Perform vendor matching on an invoice and store suggestions
     */
    async matchInvoiceVendors(invoice: Invoice): Promise<void> {
        const { storageModules } = this.deps
        const services = this.deps.getServices()
        const vendors = services.cache.getKey('vendors')
        try {
            const matches = (Object.values(vendors) as Vendor[])
                .map((vendor) => {
                    const score = this.scoreVendor(invoice.metadata, vendor)
                    return { vendor, score }
                })
                .sort((a, b) => b.score - a.score)
                .filter(
                    (match) => match.score >= LOW_CONFIDENCE_FILTER_THRESHOLD,
                )
                .slice(0, MAX_VENDOR_SUGGESTIONS)

            let invoiceUpdates: InvoiceUpdateParams = {
                invoiceId: invoice.id,
                vendorSuggestions: matches.map((match) => ({
                    vendorId: match.vendor.id,
                    confidence: match.score,
                })),
            }

            // Auto-associate vendor to invoice if confidence is high enough
            const topSuggestion = matches[0]
            if (
                topSuggestion?.score >= HIGH_CONFIDENCE_AUTO_ASSOCIATE_THRESHOLD
            ) {
                invoiceUpdates.vendorId = topSuggestion.vendor.id
                invoiceUpdates.vendorLinkConfirmed = false // though make it known that this has not been user confirmed
            }

            await storageModules.emailInvoice.updateInvoice(invoiceUpdates)
        } catch (error) {
            services.errorTracker.trackError(
                'InvoiceVendorMatchingService.matchInvoiceVendors error',
                error,
                {
                    invoiceId: invoice.id,
                },
            )
        }
    }

    /**
     * Manually associate a vendor to an invoice (user-confirmed)
     */
    async manuallyAssociateVendor(
        invoiceId: string,
        vendorId: string,
    ): Promise<void> {
        const services = this.deps.getServices()
        const { storageModules } = this.deps
        try {
            await storageModules.emailInvoice.updateInvoice({
                invoiceId,
                vendorId,
                vendorLinkConfirmed: true, // User confirmed
            })
        } catch (error) {
            services.errorTracker.trackError(
                'InvoiceVendorMatchingService.manuallyAssociateVendor error',
                error,
                {
                    invoiceId,
                    vendorId,
                },
            )
            throw error
        }
    }
}
